import {
  isBackendUnavailable,
  isHtmlResponse,
} from '@/shared/lib/utils/is-backend-unavailable';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';

const CORE_API_BASES = ['/api/auth/v3', '/api/srm/v3', '/api/monitoring/v3'];
const PROBE_PATH = '/__frontend_readiness_probe__';

const getFailedApiBase = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined;

  const requestUrl = `${error.config?.baseURL ?? ''}/${error.config?.url ?? ''}`;
  return requestUrl.match(/\/api\/[^/]+\/v\d+/)?.[0];
};

export const getProbeUrls = (initialError: unknown) => {
  const bases = new Set(CORE_API_BASES);
  const failedApiBase = getFailedApiBase(initialError);
  if (failedApiBase) bases.add(failedApiBase);

  return [...bases].map((base) => `${base}${PROBE_PATH}`);
};

export const areApiProbesAvailable = (
  results: PromiseSettledResult<unknown>[],
) =>
  results.every((result) => {
    if (result.status === 'fulfilled') return !isHtmlResponse(result.value);

    return (
      axios.isAxiosError(result.reason) &&
      Boolean(result.reason.response) &&
      !isBackendUnavailable(result.reason)
    );
  });

export const nextSuccessStreak = (current: number, available: boolean) =>
  available ? current + 1 : 0;

export const useApiHealth = (initialError: unknown) => {
  const attemptRef = useRef(0);
  const [successStreak, setSuccessStreak] = useState(0);
  const probeUrls = useMemo(() => getProbeUrls(initialError), [initialError]);

  const query = useQuery({
    queryKey: ['backend-availability', ...probeUrls],
    queryFn: async () => {
      const attempt = ++attemptRef.current;
      const results = await Promise.allSettled(
        // Global axios uses baseURL='/api'; override it to avoid /api/api/...
        probeUrls.map((url) => axios.get(url, { baseURL: '' })),
      );
      const ok = areApiProbesAvailable(results);

      return { attempt, checkedAt: Date.now(), ok };
    },
    retry: false,
    useErrorBoundary: false,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (!query.data) return;
    setSuccessStreak((current) => nextSuccessStreak(current, query.data.ok));
  }, [query.data?.checkedAt]);

  return {
    attempt: query.data?.attempt ?? 0,
    isReady: Boolean(query.data?.ok && successStreak >= 2),
  };
};